import React, { useState, useEffect } from 'react';
import { FilterPanel } from './FilterComponents';
import { ActiveFiltersSummary } from './FilterComponents';
import { ProductList } from './ProductComponents';
import SelectedProductsPanel from './SelectedProductsPanel';

const ProductListContainer = ({
    products,
    selectedProducts,
    onToggleSelect,
    onViewDetails,
    onDeepAnalysis,
    onCompare,
    isAnalysisLoading,
    isCompareLoading,
    onClearSelection
}) => {
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [filters, setFilters] = useState({
        brand: '',
        category: '',
        manufacturer: '',
        ingredients: [],
        nutritionalFilters: {},
        specialFilters: []
    });

    // Debounced filtering effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (products.length === 0) return;

            let tempProducts = [...products];

            // Brand filter
            if (filters.brand) {
                tempProducts = tempProducts.filter(p => p.extracted_brand_name === filters.brand);
            }

            // Category filter
            if (filters.category) {
                tempProducts = tempProducts.filter(p => p.category_json?.["sub-category 3"] === filters.category);
            }

            // Manufacturer filter
            if (filters.manufacturer) {
                tempProducts = tempProducts.filter(p => p.extracted_manufacturer_name === filters.manufacturer);
            }

            // Ingredients filter (multiple selection) - AND logic for non-selected products, show selected products regardless
            if (filters.ingredients && filters.ingredients.length > 0) {
                tempProducts = tempProducts.filter(p => {
                    // If product is selected, always show it
                    const isSelected = selectedProducts.some(selected => selected.product_id === p.product_id);
                    if (isSelected) {
                        return true;
                    }

                    let ingredientArray = [];
                    if (Array.isArray(p.ingredient_list)) {
                        ingredientArray = p.ingredient_list;
                    } else if (typeof p.ingredient_list === 'string' && p.ingredient_list.trim() !== '') {
                        try {
                            // Try to parse as JSON array first
                            const parsed = JSON.parse(p.ingredient_list);
                            if (Array.isArray(parsed)) {
                                ingredientArray = parsed;
                            } else {
                                // Fallback: split by semicolon
                                ingredientArray = p.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
                            }
                        } catch {
                            // Fallback: split by semicolon
                            ingredientArray = p.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
                        }
                    }

                    // Apply the same normalization as in the dropdown
                    const normalizedIngredients = [];
                    ingredientArray.forEach(ingredient => {
                        let cleanIngredient = ingredient.trim()
                            .replace(/\[[^\]]*\]/g, '') // Remove square brackets and content
                            .replace(/\([^)]*\)/g, '') // Remove parentheses and content
                            .replace(/\d+\.?\d*%?/g, '') // Remove percentages and numbers
                            .replace(/\)/g, '') // Remove standalone closing parentheses
                            .replace(/\]\)/g, '') // Remove "])" pattern
                            .replace(/& INS 0/g, '') // Remove "& INS 0" pattern
                            .trim();

                        const parts = cleanIngredient.split(',');
                        parts.forEach(part => {
                            let finalPart = part.trim();
                            if (finalPart && finalPart.length > 2) {
                                // Normalize case and plural forms (same as dropdown)
                                let normalizedPart = finalPart.toLowerCase();
                                if (normalizedPart.endsWith('s') && normalizedPart.length > 3) {
                                    normalizedPart = normalizedPart.slice(0, -1);
                                }
                                normalizedIngredients.push(normalizedPart);
                            }
                        });
                    });

                    // Check if ANY selected ingredients are present in the product (OR logic)
                    return filters.ingredients.some(selectedIngredient =>
                        normalizedIngredients.includes(selectedIngredient)
                    );
                });
            }

            // Special filters
            if (filters.specialFilters && filters.specialFilters.length > 0) {
                tempProducts = tempProducts.filter(p => {
                    return filters.specialFilters.every(filterKey => {
                        return p[filterKey] === true;
                    });
                });
            }

            // Nutritional filters
            if (filters.nutritionalFilters) {
                Object.entries(filters.nutritionalFilters).forEach(([field, filterData]) => {
                    if (filterData && filterData.value !== null && filterData.value !== '') {
                        tempProducts = tempProducts.filter(p => {
                            const productValue = p[field];
                            if (productValue === undefined || productValue === null) return true;

                            if (filterData.operator === 'gt') {
                                return productValue > filterData.value;
                            } else if (filterData.operator === 'lt') {
                                return productValue < filterData.value;
                            } else if (filterData.operator === 'eq') {
                                return Math.abs(productValue - filterData.value) < 0.1; // Allow small tolerance for equality
                            }
                            return true;
                        });
                    }
                });
            }

            setFilteredProducts(tempProducts);
        }, 200); // 500ms debounce delay for smoother experience

        return () => clearTimeout(timeoutId);
    }, [filters, products, selectedProducts]);

    const clearFilter = (filterKey) => {
        if (filterKey === 'specialFilters') {
            setFilters(prev => ({ ...prev, specialFilters: [] }));
        } else if (filterKey === 'nutritionalFilters') {
            setFilters(prev => ({ ...prev, nutritionalFilters: {} }));
        } else if (filterKey === 'ingredients') {
            setFilters(prev => ({ ...prev, ingredients: [] }));
        } else {
            setFilters(prev => ({ ...prev, [filterKey]: '' }));
        }
    };

    return (
        <>
            <FilterPanel products={products} filters={filters} setFilters={setFilters} />
            <ActiveFiltersSummary filters={filters} onClearFilter={clearFilter} />
            <div className={`main-content-grid ${selectedProducts.length > 0 ? 'selection-active' : ''}`}>
                <div className="products-section">
                    <ProductList
                        key={`productlist-${filteredProducts.length}`}
                        products={filteredProducts}
                        selectedProducts={selectedProducts}
                        onToggleSelect={onToggleSelect}
                        onViewDetails={onViewDetails}
                        totalCount={products.length}
                        filters={filters}
                        setFilters={setFilters}
                    />
                </div>
                {selectedProducts.length > 0 && (
                    <div className="selection-sidebar">
                        <SelectedProductsPanel
                            selectedProducts={selectedProducts}
                            onClearSelection={onClearSelection}
                            onViewDetails={onViewDetails}
                            onDeepAnalysis={onDeepAnalysis}
                            onCompare={onCompare}
                            isAnalysisLoading={isAnalysisLoading}
                            isCompareLoading={isCompareLoading}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default ProductListContainer;
